/**
 * Company Profile Page
 * หน้าข้อมูลบริษัท - Matching reference design
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router-dom'
import {
  useCompany,
  useCompanyProjects,
  useCompanyDocuments,
  useChangeHistory,
  useActivityLog,
} from './'
import { Button } from '@/components/ui/button'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import {
  ArrowLeft,
  Edit,
  Trash2,
  ChevronRight,
  FileText,
  Download,
  FileUp,
  Clock,
  Plus,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { usePermissions } from '@/features/auth/usePermissions'
import { CompanyProfileSkeleton } from './components/CompanyProfileSkeleton'
import { CompanyProfileError } from './components/CompanyProfileError'
import { EditCompanyDialog } from './components/EditCompanyDialog'
import { DeleteCompanyDialog } from './components/DeleteCompanyDialog'
import type { CompanyProject, CompanyDocument } from './types'

// Format date based on current language
function formatDate(
  dateString: string,
  i18n: { language: string },
  options: Intl.DateTimeFormatOptions = {}
): string {
  const date = new Date(dateString)
  if (isNaN(date.getTime())) return dateString

  const isThai = i18n.language === 'th'

  return new Intl.DateTimeFormat(isThai ? 'th-TH' : 'en-US', {
    day: 'numeric',
    month: 'short',
    year: isThai ? 'numeric' : '2-digit',
    ...options,
  }).format(date)
}

// Status badge for projects
function ProjectStatusBadge({ status }: { status: CompanyProject['status'] }) {
  const { t } = useTranslation()

  const styles = {
    'in-progress': 'bg-[var(--status-in-progress-bg)] text-[var(--status-in-progress-text)]',
    completed: 'bg-[var(--status-completed-bg)] text-[var(--status-completed-text)]',
    'not-started': 'bg-[var(--status-not-started-bg)] text-[var(--status-not-started-text)]',
  }

  return (
    <span
      className={cn(
        'inline-flex items-center rounded px-2 py-0.5 text-[10px] font-medium',
        styles[status]
      )}
    >
      {t(`status.${status}`)}
    </span>
  )
}

// Report status badge
function ReportStatusBadge({ status }: { status: CompanyProject['reportStatus'] }) {
  const { t } = useTranslation()

  if (status === 'none') return <span className="text-muted-foreground">—</span>

  const styles = {
    issued: 'bg-[var(--badge-report-issued-bg)] text-[var(--badge-report-issued-text)]',
    draft: 'bg-[var(--badge-report-draft-bg)] text-[var(--badge-report-draft-text)]',
  }

  return (
    <span
      className={cn(
        'inline-flex items-center rounded px-2 py-0.5 text-[10px] font-medium',
        styles[status]
      )}
    >
      {t(`reportStatus.${status}`)}
    </span>
  )
}

// Document source badge
function DocumentSourceBadge({ source }: { source: CompanyDocument['source'] }) {
  const { t } = useTranslation()

  const styles = {
    initial: 'bg-muted text-muted-foreground',
    change: 'bg-[var(--status-in-progress-bg)] text-[var(--status-in-progress-text)]',
  }

  return (
    <span
      className={cn(
        'inline-flex items-center rounded px-2 py-0.5 text-[10px] font-medium',
        styles[source]
      )}
    >
      {t(`companies.documents.${source}Badge`)}
    </span>
  )
}

// Projects Section
function ProjectsSection({ companyId }: { companyId: string }) {
  const { t, i18n } = useTranslation()
  const { data: projects, isLoading } = useCompanyProjects(companyId)

  if (isLoading) return <LoadingSpinner size="sm" />

  const projectList = projects ?? []

  return (
    <div className="bg-card rounded-lg border">
      <div className="flex items-center justify-between border-b px-4 py-2">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold">{t('companies.tabs.projects')}</h3>
          <span className="text-muted-foreground text-[11px]">{t('project.projectHistory')}</span>
        </div>
        <span className="text-muted-foreground text-sm">
          {projectList.length} {t('project.projectsUnit')}
        </span>
      </div>

      <table className="w-full">
        <thead>
          <tr className="bg-muted/30 border-b">
            <th className="text-muted-foreground px-4 py-2 text-left text-[11px] font-semibold tracking-wider uppercase">
              {t('project.year')}
            </th>
            <th className="text-muted-foreground px-4 py-2 text-left text-[11px] font-semibold tracking-wider uppercase">
              {t('project.status')}
            </th>
            <th className="text-muted-foreground px-4 py-2 text-left text-[11px] font-semibold tracking-wider uppercase">
              {t('project.auditor')}
            </th>
            <th className="text-muted-foreground px-4 py-2 text-left text-[11px] font-semibold tracking-wider uppercase">
              {t('project.report')}
            </th>
            <th className="text-muted-foreground px-4 py-2 text-left text-[11px] font-semibold tracking-wider uppercase">
              {t('project.workspace')}
            </th>
            <th className="text-muted-foreground px-4 py-2 text-left text-[11px] font-semibold tracking-wider uppercase">
              {t('project.updated')}
            </th>
            <th className="text-muted-foreground px-4 py-2 text-left text-[11px] font-semibold tracking-wider uppercase"></th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {projectList.map((project) => (
            <tr key={project.id} className="hover:bg-muted/20">
              <td className="text-primary px-4 py-2 text-xs font-bold">{project.year}</td>
              <td className="px-4 py-2">
                <ProjectStatusBadge status={project.status} />
              </td>
              <td className="px-4 py-2 text-xs">{project.auditor}</td>
              <td className="px-4 py-2">
                <ReportStatusBadge status={project.reportStatus} />
              </td>
              <td className="px-4 py-2 text-xs">{project.workspace ?? '-'}</td>
              <td className="text-muted-foreground px-4 py-2 text-[10px]">
                {formatDate(project.updated, i18n)}
              </td>
              <td className="px-4 py-2">
                <Button
                  asChild
                  size="sm"
                  variant="outline"
                  className="border-primary text-primary hover:bg-primary/5 h-7 text-[10px] font-bold uppercase transition-all"
                >
                  <Link to={`/projects/${project.id}`}>
                    {t('project.open')}
                    <ChevronRight className="ml-0.5 h-3 w-3" />
                  </Link>
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="border-t p-4">
        <Button className="bg-primary hover:bg-primary/90 w-full">
          <Plus className="mr-1 h-4 w-4" />
          {t('companies.createProject')}
        </Button>
      </div>
    </div>
  )
}

// Documents Section
function DocumentsSection({ companyId }: { companyId: string }) {
  const { t, i18n } = useTranslation()
  const { data: documents, isLoading } = useCompanyDocuments(companyId)
  const [filter, setFilter] = useState<'all' | 'initial' | 'change'>('all')

  if (isLoading) return <LoadingSpinner size="sm" />

  const docList = documents ?? []
  const filteredDocs = filter === 'all' ? docList : docList.filter((d) => d.source === filter)

  return (
    <div className="bg-card rounded-lg border">
      <div className="flex items-center justify-between border-b px-4 py-2">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold">{t('companies.tabs.documents')}</h3>
          <span className="text-muted-foreground text-[11px]">{t('companies.documentsTitle')}</span>
        </div>
        <span className="text-muted-foreground text-[11px]">
          {docList.length} {t('companies.documentsCount', { count: docList.length })}
        </span>
      </div>

      <div className="flex items-center gap-2 border-b px-4 py-2">
        {(['all', 'initial', 'change'] as const).map((key) => (
          <button
            key={key}
            onClick={() => {
              setFilter(key)
            }}
            className={cn(
              'rounded-full px-3 py-1 text-sm transition-colors',
              filter === key
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-muted'
            )}
          >
            {t(`companies.documents.${key}`)}
          </button>
        ))}
        <div className="flex-1"></div>
        <Button size="sm" variant="outline">
          <FileUp className="mr-1 h-4 w-4" />
          {t('companies.uploadDocument')}
        </Button>
      </div>

      <table className="w-full">
        <thead>
          <tr className="bg-muted/30 border-b">
            <th className="text-muted-foreground px-4 py-2 text-left text-[11px] font-semibold tracking-wider uppercase">
              {t('companies.documents.name')}
            </th>
            <th className="text-muted-foreground px-4 py-2 text-left text-[11px] font-semibold tracking-wider uppercase">
              {t('companies.documents.source')}
            </th>
            <th className="text-muted-foreground px-4 py-2 text-left text-[11px] font-semibold tracking-wider uppercase">
              {t('companies.documents.type')}
            </th>
            <th className="text-muted-foreground px-4 py-2 text-left text-[11px] font-semibold tracking-wider uppercase">
              {t('companies.documents.size')}
            </th>
            <th className="text-muted-foreground px-4 py-2 text-left text-[11px] font-semibold tracking-wider uppercase">
              {t('companies.documents.uploaded')}
            </th>
            <th className="text-muted-foreground px-4 py-2 text-left text-[11px] font-semibold tracking-wider uppercase">
              {t('companies.documents.by')}
            </th>
            <th className="text-muted-foreground px-4 py-2 text-left text-[11px] font-semibold tracking-wider uppercase">
              {t('companies.documents.version')}
            </th>
            <th className="text-muted-foreground px-4 py-2 text-left text-[11px] font-semibold tracking-wider uppercase"></th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {filteredDocs.map((doc) => (
            <tr key={doc.id} className="hover:bg-muted/20">
              <td className="px-4 py-2">
                <div className="flex items-center gap-2">
                  <FileText className="text-muted-foreground h-3 w-3" />
                  <span className="text-primary text-xs font-medium">{doc.name}</span>
                </div>
              </td>
              <td className="px-4 py-2">
                <DocumentSourceBadge source={doc.source} />
              </td>
              <td className="px-4 py-2">
                <span className="rounded border border-red-100 bg-red-50 px-1.5 py-0.5 text-[10px] font-bold text-red-600 uppercase">
                  PDF
                </span>
              </td>
              <td className="text-muted-foreground px-4 py-2 text-[10px]">{doc.size}</td>
              <td className="text-muted-foreground px-4 py-2 text-[10px]">
                {formatDate(doc.date, i18n)}
              </td>
              <td className="px-4 py-2 text-xs">{doc.by}</td>
              <td className="px-4 py-2">
                <span className="bg-muted rounded px-2 py-0.5 font-mono text-[10px]">
                  v{doc.version}
                </span>
              </td>
              <td className="px-4 py-2">
                <div className="flex items-center gap-1">
                  <Button size="icon" variant="ghost" className="h-8 w-8">
                    <Download className="h-4 w-4" />
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// History Section
function HistorySection({ companyId }: { companyId: string }) {
  const { t, i18n } = useTranslation()
  const { data: history, isLoading } = useChangeHistory(companyId)

  if (isLoading) return <LoadingSpinner size="sm" />

  const historyList = history ?? []

  return (
    <div className="bg-card rounded-lg border">
      <div className="flex items-center justify-between border-b px-4 py-2">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold">{t('companies.tabs.history')}</h3>
          <span className="text-muted-foreground text-[11px]">
            {t('companies.history.subtitle')}
          </span>
        </div>
      </div>

      <div className="space-y-6 p-4">
        {historyList.length === 0 ? (
          <p className="text-muted-foreground py-8 text-center">
            {t('companies.history.noHistory')}
          </p>
        ) : (
          historyList.map((entry, index) => (
            <div key={entry.id} className="relative pl-6">
              {/* Timeline dot */}
              <div
                className={cn(
                  'absolute top-0 left-0 h-3 w-3 rounded-full border-2',
                  index === 0
                    ? 'border-primary bg-primary'
                    : 'border-muted-foreground bg-background'
                )}
              />

              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <span className="font-medium">
                    {formatDate(entry.date, i18n, {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })}
                  </span>
                  <span className="text-muted-foreground">
                    {t('companies.documents.by')} {entry.by}
                  </span>
                  <span className="text-muted-foreground">
                    {t('companies.history.via')} {entry.via}
                  </span>
                </div>

                {entry.changes.map((change, idx) => (
                  <div key={idx} className="bg-muted/50 rounded-lg p-3">
                    <p className="mb-2 text-sm font-medium">{change.field}</p>
                    {change.oldValue !== null && (
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-destructive line-through">{change.oldValue}</span>
                        <span className="text-muted-foreground">→</span>
                        <span className="text-green-600">{change.newValue}</span>
                      </div>
                    )}
                    {change.oldValue === null && (
                      <p className="text-sm text-green-600">{change.newValue}</p>
                    )}
                  </div>
                ))}

                {entry.documents.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {entry.documents.map((doc, docIdx) => (
                      <span
                        key={docIdx}
                        className="inline-flex items-center gap-1 rounded bg-blue-50 px-2 py-1 text-xs text-blue-600"
                      >
                        <FileText className="h-3 w-3" />
                        {doc}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
}

// Activity Section
function ActivitySection({ companyId }: { companyId: string }) {
  const { t, i18n } = useTranslation()
  const { data: activities, isLoading } = useActivityLog(companyId)

  if (isLoading) return <LoadingSpinner size="sm" />

  const activityList = activities ?? []

  return (
    <div className="bg-card rounded-lg border">
      <div className="flex items-center justify-between border-b px-4 py-2">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold">{t('companies.tabs.activity')}</h3>
          <span className="text-muted-foreground text-[11px]">{t('companies.activityTitle')}</span>
        </div>
      </div>

      <div className="space-y-4 p-4">
        {activityList.length === 0 ? (
          <p className="text-muted-foreground py-8 text-center">
            {t('companies.activity.noActivity')}
          </p>
        ) : (
          activityList.map((activity, index) => (
            <div key={index} className="relative pl-6">
              <div className="bg-muted-foreground absolute top-1 left-0 h-2 w-2 rounded-full" />
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{activity.user}</span>
                  <span className="text-sm">{activity.action}</span>
                  {activity.isFormal && (
                    <span className="rounded bg-[var(--status-in-progress-bg)] px-1.5 py-0.5 text-xs text-[var(--status-in-progress-text)]">
                      {t('companies.activity.formalChange')}
                    </span>
                  )}
                </div>
                <p className="text-muted-foreground flex items-center gap-1 text-xs">
                  <Clock className="h-3 w-3" />
                  {formatDate(activity.time, i18n, {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </p>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
}

export function CompanyProfilePage() {
  const { t, i18n } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const { can } = usePermissions()
  const [activeTab, setActiveTab] = useState<'projects' | 'documents' | 'history' | 'activity'>(
    'projects'
  )
  const [editOpen, setEditOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)

  const { data: company, isLoading, error, refetch } = useCompany(id)

  if (isLoading) return <CompanyProfileSkeleton />

  if (error || !company) {
    return (
      <CompanyProfileError
        error={error}
        onRetry={() => {
          void refetch()
        }}
      />
    )
  }

  const tabs = [
    { key: 'projects', label: t('companies.tabs.projects') },
    { key: 'documents', label: t('companies.tabs.documents') },
    { key: 'history', label: t('companies.tabs.history') },
    { key: 'activity', label: t('companies.tabs.activity') },
  ] as const

  return (
    <div className="container mx-auto p-6">
      {/* Breadcrumb */}
      <div className="mb-4">
        <Link
          to="/companies"
          className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-sm"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('nav.companies')}
        </Link>
      </div>

      {/* Header */}
      <div className="mb-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-foreground text-xl font-bold">{company.nameTh}</h1>
            <p className="text-muted-foreground text-xs">{company.nameEn}</p>
          </div>
          <div className="flex items-center gap-2">
            {can('companies:update') && (
              <Button
                variant="outline"
                className="border-primary text-primary hover:bg-primary/5"
                onClick={() => {
                  setEditOpen(true)
                }}
              >
                <Edit className="mr-1 h-4 w-4" />
                {t('actions.edit')}
              </Button>
            )}
            {can('companies:delete') && (
              <Button
                variant="outline"
                className="border-destructive text-destructive hover:bg-destructive/5"
                onClick={() => {
                  setDeleteOpen(true)
                }}
              >
                <Trash2 className="mr-1 h-4 w-4" />
                {t('actions.delete')}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Company Info Card */}
      <div className="bg-card mb-6 rounded-lg border p-6">
        <div className="grid gap-6 md:grid-cols-3">
          {/* Column 1 */}
          <div className="space-y-3">
            <div>
              <p className="text-muted-foreground mb-1 text-[10px] font-semibold tracking-wider uppercase">
                • {t('companies.fields.registrationNo')}
              </p>
              <p className="text-primary text-sm font-bold">{company.registrationNo}</p>
            </div>
            <div>
              <p className="text-muted-foreground mb-1 text-[10px] font-semibold tracking-wider uppercase">
                • {t('companies.fields.businessType')}
              </p>
              <p className="text-xs font-medium">{company.businessType}</p>
            </div>
            <div>
              <p className="text-muted-foreground mb-1 text-[10px] font-semibold tracking-wider uppercase">
                {t('companies.fields.phone')}
              </p>
              <p className="text-xs">{company.phone}</p>
            </div>
          </div>

          {/* Column 2 */}
          <div className="space-y-3">
            <div>
              <p className="text-muted-foreground mb-1 text-[10px] font-semibold tracking-wider uppercase">
                • {t('companies.fields.taxId')}
              </p>
              <p className="text-primary text-sm font-bold">{company.taxId}</p>
            </div>
            <div>
              <p className="text-muted-foreground mb-1 text-[10px] font-semibold tracking-wider uppercase">
                • {t('companies.fields.directors')}
              </p>
              <p className="text-xs font-medium">{company.directors}</p>
            </div>
            <div>
              <p className="text-muted-foreground mb-1 text-[10px] font-semibold tracking-wider uppercase">
                {t('companies.fields.email')}
              </p>
              <p className="text-primary decoration-primary/30 text-xs font-medium underline underline-offset-2">
                {company.email}
              </p>
            </div>
          </div>

          {/* Column 3 */}
          <div className="space-y-3">
            <div>
              <p className="text-muted-foreground mb-1 text-[10px] font-semibold tracking-wider uppercase">
                • {t('companies.fields.registeredCapital')}
              </p>
              <p className="text-primary text-sm font-bold">
                {Number(company.registeredCapital).toLocaleString('th-TH')} {t('common.baht')}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground mb-1 text-[10px] font-semibold tracking-wider uppercase">
                • {t('companies.fields.address')}
              </p>
              <p className="text-xs leading-relaxed">{company.address}</p>
            </div>
            <div>
              <p className="text-muted-foreground mb-1 text-[10px] font-semibold tracking-wider uppercase">
                {t('companies.fields.contactPerson')}
              </p>
              <p className="text-xs font-medium">
                {company.contactPerson}{' '}
                <span className="text-muted-foreground font-normal">({company.contactPhone})</span>
              </p>
            </div>
          </div>
        </div>

        {/* Footer info */}
        <div className="border-border/50 text-muted-foreground mt-4 flex items-center justify-between border-t pt-3 text-[10px]">
          <p>
            {t('companies.fields.createdAt')}:{' '}
            {formatDate(company.createdAt, i18n, { month: 'long', year: 'numeric' })}
          </p>
          <div className="flex items-center gap-4">
            <p>
              {t('companies.fields.updatedAt')}:{' '}
              {formatDate(company.updatedAt, i18n, { month: 'long', year: 'numeric' })}
            </p>
            <span className="text-primary/80 flex items-center gap-1 font-medium">
              <span className="bg-primary/80 h-1.5 w-1.5 rounded-full"></span>
              {t('companies.fields.changedData')}
            </span>
            <span className="text-muted-foreground">
              ◆ {t('companies.fields.evidenceRequiredShort')}
            </span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-4 flex items-center gap-1 border-b">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => {
              setActiveTab(tab.key)
            }}
            className={cn(
              'relative px-4 py-2 text-xs font-bold tracking-wider uppercase transition-colors',
              activeTab === tab.key ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
            )}
          >
            {tab.label}
            {activeTab === tab.key && (
              <span className="bg-primary absolute right-0 bottom-0 left-0 h-0.5" />
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="space-y-6">
        {activeTab === 'projects' && <ProjectsSection companyId={company.id} />}
        {activeTab === 'documents' && <DocumentsSection companyId={company.id} />}
        {activeTab === 'history' && <HistorySection companyId={company.id} />}
        {activeTab === 'activity' && <ActivitySection companyId={company.id} />}
      </div>

      {/* Edit Dialog */}
      {can('companies:update') && editOpen && (
        <EditCompanyDialog
          open={editOpen}
          onClose={() => {
            setEditOpen(false)
          }}
          company={company}
        />
      )}

      {/* Delete Dialog */}
      {can('companies:delete') && deleteOpen && (
        <DeleteCompanyDialog
          open={deleteOpen}
          onClose={() => {
            setDeleteOpen(false)
          }}
          company={company}
          redirectAfter="/companies"
        />
      )}
    </div>
  )
}
